/**
 * "How this figure is calculated" for the generated reports — the demo's
 * `calc` notes at lines 2597, 2603 and 2620, with the steps worked from the
 * same rows the source drawer opens underneath.
 *
 * Each builder takes the rows and the figure the report printed. Where the
 * figure comes from the precomputed weekly pack rather than from the rows, the
 * last step says so, so a reader who re-adds the rows can see why the two
 * might differ.
 *
 * Pure: no React, no clock.
 *
 * @see src/versions/v1/shared/source-calc.ts — the notes, verbatim
 */

import type { SourceCalc } from '../../chrome-oem/SourceDrawerBody';
import { ENERGY_FACTORS, BY_ID } from '../../data/core';
import type { ShiftDailyRow } from '../../schema/telemetry';
import type { WorkOrder } from '../../schema/work-order';
import { fmt$, groupNum } from '../../shared/oem-engine';
import { CALC_NOTE } from '../../shared/source-calc';

const round1 = (n: number): number => +n.toFixed(1);

/** Lines 2597 / 2603 / 2620 — availability over a set of shift records. */
export function availabilityCalc(
  rows: readonly ShiftDailyRow[],
  shown: number,
  note: 'reportAvailability' | 'deckAvailability' = 'reportAvailability',
): SourceCalc {
  const hours = rows.reduce((a, s) => a + s.hours, 0);
  const scheduled = rows.length * 2 * 9.5;
  return {
    formula: CALC_NOTE[note],
    steps: rows.length
      ? [
          `Σ engine hours = ${groupNum(round1(hours))} h over ${rows.length} machine-days`,
          `${groupNum(round1(hours))} ÷ (${rows.length} × 2 × 9.5 = ${groupNum(scheduled)} h) = ${((hours / scheduled) * 100).toFixed(1)}%`,
          `Report figure: ${shown}%`,
        ]
      : ['No shift records in the reporting week.'],
  };
}

/** Line 2603 — a mine's cost this week, from its work orders. */
export function costCalc(rows: readonly WorkOrder[], shown: number): SourceCalc {
  const direct = rows.reduce((a, w) => a + w.total_direct_cost_aud, 0);
  const lost = rows.reduce((a, w) => a + w.downtime_cost_aud, 0);
  return {
    formula: CALC_NOTE.reportCost,
    steps: [
      `Σ direct (labour + parts + freight + external) = ${fmt$(direct)} over ${rows.length} orders`,
      `Σ lost production = ${fmt$(lost)}`,
      `Total = ${fmt$(direct + lost)} · report figure ${fmt$(shown)} (weekly pack)`,
    ],
  };
}

/** Line 2620 — CO₂-e this week over a set of shift records. */
export function emissionsCalc(rows: readonly ShiftDailyRow[], shown: number): SourceCalc {
  const diesel = rows.reduce((a, s) => a + s.diesel_l, 0);
  const kwh = rows.reduce((a, s) => a + s.kwh, 0);
  const grid = rows.reduce((a, s) => {
    const f = ENERGY_FACTORS.find((x) => x.mine === BY_ID[s.machine_id]?.mine);
    return a + s.kwh * (f?.grid_kgco2_per_kwh ?? 0);
  }, 0);
  const kg = rows.reduce((a, s) => a + s.co2_kg, 0);
  return {
    formula: CALC_NOTE.deckEmissions,
    steps: [
      `Diesel: ${groupNum(Math.round(diesel))} L × 2.68 = ${groupNum(Math.round(diesel * 2.68))} kg`,
      `Electricity: ${groupNum(Math.round(kwh))} kWh × site grid factor = ${groupNum(Math.round(grid))} kg`,
      `Σ CO₂-e on the shift records = ${groupNum(Math.round(kg))} kg ÷ 1000 = ${round1(kg / 1000)} t · report figure ${shown} t`,
    ],
  };
}
